import { Component, OnInit, OnDestroy, HostListener } from '@angular/core';
import * as _ from 'lodash';
import * as Chart from 'chart.js';
import { PondCultureService } from 'src/app/core/services/pond-culture.service';
import { BusinessEntityService } from 'src/app/core/services/business.service';
import { FarmSiteService } from 'src/app/core/services/farm-site.service';
import { PondSummaryReportService } from '../../../core/services/pond-summary.service';
import PondSummaryReportModel from '../../../core/models/pond-summary.model';
import { environment } from '../../../../environments/environment';
import BusinessEntity from 'src/app/core/models/business.model';
import FarmSiteModel from 'src/app/core/models/farm-site.model';
import PondCultureModel from 'src/app/core/models/pond-culture.model';
import { ActivatedRoute } from '@angular/router';
import { ExcelService } from 'src/app/core/services/excel.service';
import PersonsModel from '../../../core/models/persons.model';
import { PersonsService } from '../../../core/services/persons.service';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { CommonMethodsService } from 'src/app/core/services/common-methods.service';
import { LocationStrategy } from '@angular/common';
import { PreviousRouteService } from 'src/app/core/services/previous-route.service';
import { BatchLoader } from 'src/app/core/utilities/batch-loader.util';
import { USER_SESSION_SERVICE } from 'src/app/core/rbacl/user-session.service';
import { BUSINESS_CATEGORY_KEYS, PERSON_ROLE_KEYS } from 'src/app/core/constants/app-constants';

declare var $: any;

/**
 * @component PondsSummaryReportsComponent
 * @selector app-ponds-summary-reports
 * @implements {OnInit}
 * @implements {OnDestroy}
 *
 * @description
 * The `PondsSummaryReportsComponent` is a comprehensive reporting interface designed to provide a detailed summary
 * of pond performance and culture metrics within a farm. It serves as a critical tool for farm managers, owners,
 * and technicians to monitor the status of aquaculture ponds.
 *
 * ## Key Responsibilities:
 * 1. **Data Aggregation**: Fetches and aggregates summary data for ponds based on selected farm sites.
 * 2. **Visual Analytics**: Integrates with `Chart.js` to provide visual representations of key metrics:
 *    - Feed Log vs. Days of Culture (DOC).
 *    - Average Body Weight (ABW) vs. DOC.
 * 3. **Filtering & Search**: Offers client-side filtering capabilities to search through pond summaries by:
 *    - Section Name
 *    - Pond Name
 *    - Harvest Mode
 *    - Hatchery Name
 *    - Breeder & Breed Line
 * 4. **Role-Based Context**: Dynamically adjusts the available data and filtering options based on the user's role
 *    (e.g., Farm Owner, Farm Manager, Provider, Employee).
 * 5. **Export Functionality**: Allows users to export the summary report to Excel for offline analysis.
 * 6. **Performance Optimization**: Utilizes a `BatchLoader` to handle large datasets efficiently via infinite scrolling.
 *
 * ## User Roles & Permissions:
 * - **Farm Category**:
 *   - **Business Admin / Farmer**: Access to all farm sites within the business.
 *   - **Farm Manager**: Restricted access to farm sites assigned to them (via Farm Spend context).
 * - **Provider Category**:
 *   - **Business Admin**: Access to farm sites associated with the provider business.
 *   - **Provider Manager**: Access to farm sites managed by technicians under their supervision.
 * - **Employee / Other**: Requires manual selection of a business entity to view reports.
 *
 * ## Data Flow:
 * 1. **Initialization**: `ngOnInit` determines the user context and fetches the appropriate list of Farm Sites or Business Entities.
 * 2. **Selection**: User selects a Business (if applicable) and a Farm Site.
 * 3. **Data Fetching**: `pondSummary()` is triggered to fetch the report data from `PondSummaryReportService`.
 * 4. **Display**: Data is loaded into `pondSummaryReports` via `BatchLoader` for pagination.
 * 5. **Drill-down**: Clicking on a specific entry triggers `goToPondsummaryGraph()`, which fetches detailed culture data and renders charts.
 *
 * ## UI State Management:
 * - **Loading States**: Managed via `responseLoader` (API calls) and `listLoader` (infinite scroll).
 * - **Form Validation**: Managed via `registerForm`.
 * - **Charts**: Canvas elements are dynamically updated based on the selected pond's data.
 */
@Component({
  selector: 'app-ponds-summary',
  templateUrl: './ponds-summary.component.html',
  styleUrls: ['./ponds-summary.component.scss']
})
export class PondsSummaryReportsComponent implements OnInit, OnDestroy {
  /** @type {Array<any>} The list of pond summary reports currently displayed in the UI. */
  public pondSummaryReports: Array<any>;

  /** @type {PondSummaryReportModel} The model containing filter criteria (business_id, farm_site_id). */
  public pondSummaryReport: PondSummaryReportModel;

  /** @type {Array<PondSummaryReportModel>} Placeholder for original report data (unused in current logic). */
  public originalPondSummaryReport: Array<PondSummaryReportModel>;

  /** @type {Array<any>} The master list of all fetched reports, used for client-side filtering. */
  public dupPondSummaryReports: Array<any>;

  /** @type {Array<BusinessEntity>} List of available business entities for selection (non-farm users). */
  public businessEntities: Array<BusinessEntity>;

  /** @type {BusinessEntity} The currently selected business entity. */
  public businessEntity: BusinessEntity;

  /** @type {Array<FarmSiteModel>} List of farm sites available for selection. */
  public farmSites: Array<FarmSiteModel>;

  /** @type {PondCultureModel} Detailed culture data for a specific pond, used for charting. */
  public pondCulture: PondCultureModel;

  /** @type {boolean} Flag indicating if the search/submit button has been clicked. */
  public isSearchClicked: boolean;

  /** @type {boolean} Flag controlling the main loading spinner for API requests. */
  public responseLoader: boolean;

  /** @type {boolean} Flag to disable the submit button during processing. Default is `false`. */
  public submitButtonDisabled: boolean = false;

  /** @type {string} Name of the file for export (unused in current logic). */
  public file_name: string;

  /** @type {any} Reference to the canvas element for Chart.js. */
  public canvas: any;

  /** @type {any} Reference to the 2D context of the canvas. */
  public ctx: any;

  /** @type {any} Document reference (unused in current logic). */
  public doc: any;

  /** @type {boolean} Flag indicating if there is no data for the main chart. */
  public myChartNodata: boolean;

  /** @type {boolean} Flag indicating if there is no data for the sampling chart. */
  public mySamplingvsDocData: boolean;

  // sorting pagination
  /** @type {string} Current sort key. Default is 'name'. */
  public key: string = 'name';

  /** @type {boolean} Current sort direction (reverse). Default is `false`. */
  public reverse: boolean = false;

  /** @type {boolean} Flag for sorting by Section Name. */
  public psummarysectionnameAsc: boolean = false;

  /** @type {boolean} Flag for sorting by Pond Name. */
  public psummaryPondnameAsc: boolean = false;

  /** @type {any} Application permissions object. */
  public appPermission: any;

  /**
   * @method sort
   * @description Sets the sort key and toggles the sort direction.
   * @param {string} key - The property name to sort by.
   */
  sort(key: string) {
    this.key = key;
    this.reverse = !this.reverse;
  }

  // pagination sorting
  /** @type {number} Current page number for pagination. Default is `1`. */
  currentPageNumber: number = 1;

  /** @type {number} Number of items per page. Default is `10`. */
  noOfItemsPerPage: number = 10;

  /* End pagination, sorting, filter*/
  /** @type {string} Keyword for search operations. */
  public keyword: string;

  /** @type {any} Generic item holder. */
  public item: any;

  /** @type {string} The current search string entered by the user. */
  public search: string;

  /** @type {boolean} Flag to display a "No Data" message. Default is `false`. */
  public NoDataMessage: boolean = false;

  /** @type {number} The current active page number. */
  public activePageNo: number;

  /** @type {boolean} Flag to trigger clearing of the search input. */
  public clearSearchInput: boolean;

  /** @type {Array<PersonsModel>} List of technicians (used for Provider Manager role). */
  public persons: Array<PersonsModel>;

  /** @type {FormGroup} The main reactive form for business and location selection. */
  public registerForm: FormGroup;

  /** @type {boolean} Flag controlling the visibility of the business selection dropdown. Default is `false`. */
  public isDropdownOpen = false;

  /** @type {any[]} Filtered list of options for the business autocomplete dropdown. Default is `[]`. */
  public filteredOptions: any[] = [];

  /** @type {string} Placeholder or value for the business search input. Default is 'Enter the business name'. */
  public businessSearch: string = 'Enter the business name';

  /** @type {boolean} Flag indicating if the form has been submitted. */
  public submitted: boolean;

  /** @type {string} The type of filter currently active. */
  public filterType: string;

  /** @type {boolean} Flag indicating if any filters have been applied. */
  public filtersApplied: boolean;

  /** @type {boolean} Flag indicating if the submit action has been triggered successfully. */
  public isSubmitClicked: boolean;

  /** @type {number} Duplicate tracker for current page number. Default is `1`. */
  public dupCurrentPageNo: number = 1;

  /** @type {boolean} Flag controlling the spinner for list batch loading. Default is `false`. */
  public listLoader = false;              // Show listLoader spinner

  /** @type {number} The starting index for batch loading. Default is `0`. */
  public startIndex = 0;             // Tracks how much has been loaded

  /** @type {boolean} Flag indicating if client-side filtering is currently active. Default is `false`. */
  public isFiltering: boolean = false;

  /** @type {boolean} Flag indicating if the list is empty. Default is `false`. */
  public noList: boolean = false;

  /** @type {BatchLoader<any>} Utility for handling batch loading of data. */
  private batchLoader: BatchLoader<any>;

  /**
   * Constructor for PondsSummaryReportsComponent.
   * Initializes the component and injects necessary services.
   *
   * @param {FormBuilder} formBuilder - Service for building reactive forms.
   * @param {ExcelService} excelService - Service for exporting data to Excel.
   * @param {PersonsService} personService - Service for managing person/technician data.
   * @param {ActivatedRoute} route - Service for retrieving route information.
   * @param {BusinessEntityService} businessEntityService - Service for business entity operations.
   * @param {FarmSiteService} farmSitesService - Service for farm site operations.
   * @param {PondCultureService} pondCultureService - Service for pond culture data.
   * @param {PondSummaryReportService} pondSummaryService - Service for fetching summary reports.
   * @param {CommonMethodsService} commonMethodsService - Service for common utility methods.
   * @param {LocationStrategy} locationStrategy - Service for interacting with the browser's URL.
   * @param {PreviousRouteService} previousRouteService - Service for tracking route history.
   *
   * @service FormBuilder
   * @service ExcelService
   * @service PersonsService
   * @service ActivatedRoute
   * @service BusinessEntityService
   * @service FarmSiteService
   * @service PondCultureService
   * @service PondSummaryReportService
   * @service CommonMethodsService
   * @service LocationStrategy
   * @service PreviousRouteService
   */
  constructor(public formBuilder: FormBuilder, public excelService: ExcelService, public personService: PersonsService, public route: ActivatedRoute, public businessEntityService: BusinessEntityService, public farmSitesService: FarmSiteService, public pondCultureService: PondCultureService, public pondSummaryService: PondSummaryReportService,
    public commonMethodsService: CommonMethodsService, private locationStrategy: LocationStrategy, private previousRouteService: PreviousRouteService) {
    this.pondSummaryReport = new PondSummaryReportModel();
    // Initialize the main register form with validation
    this.registerForm = this.formBuilder.group({
      businessfield: ['', Validators.required],
      locationField: ['', Validators.required]
    });
  }

  /**
   * @lifecycle ngOnInit
   * @async
   * @description
   * Lifecycle hook that is called after data-bound properties of a directive are initialized.
   * - Sets up the previous route.
   * - Determines the logic flow based on `loginBusinessCategory`:
   *   - **Farm**: Auto-sets business ID and fetches farm sites based on `loginPersonType`.
   *   - **Provider**: Fetches farm sites or technicians based on `loginPersonType`.
   *   - **Default**: Fetches all business entities for selection.
   * - Adjusts form validation based on user role.
   *
   * @throws {Error} Captures and displays errors via `commonMethodsService` if initialization fails.
   */
  public async ngOnInit() {
    try {
      this.search = '';
      this.filterType = '';
      this.filtersApplied = false;
      this.isSubmitClicked = false;
      window.scrollTo(0, 0);
      this.previousRouteService.setPreviousRoute(this.locationStrategy.path());
      switch (USER_SESSION_SERVICE.currentBusinessCategoryName) {
        case BUSINESS_CATEGORY_KEYS.PROVIDER:
          switch (USER_SESSION_SERVICE.currentUserRole) {
            case PERSON_ROLE_KEYS.BUSINESS_ADMIN:
              this.pondSummaryReport.business_id = USER_SESSION_SERVICE.currentBusinessId;
              this.farmSites = await this.farmSitesService.getFarmeSitesBusinessDetailsByProviderId(USER_SESSION_SERVICE.currentBusinessId);
              if (this.farmSites.length === 1) {
                this.pondSummaryReport.farm_site_id = this.farmSites[0].id; // model-binding key
              }
              this.registerForm.get('businessfield').setValidators(null);
              this.registerForm.get('businessfield').updateValueAndValidity();
              break;
            default:
              this.persons = await this.personService.getAllTechniciansByProviderManagerId(USER_SESSION_SERVICE.currentUserId);
              break;
          }
          break;
        default:
          this.businessSearch = undefined;
          this.registerForm.get('businessfield').patchValue('');
          let businesses = await this.businessEntityService.getAllBusiness();
          this.businessEntities = businesses.filter(obj => {
            return ((obj.businessType && obj.businessType.length > 0 && obj.businessType[0].businessTypeID && obj.businessType[0].businessTypeID != '618a18ac0f339c001e991b08') && (obj.businessType && obj.businessType.length > 0 && obj.businessType[0].businessTypeID && obj.businessType[0].businessTypeID != '62b3fa8998be51001d17a310'));
          });
          this.filteredOptions = this.businessEntities;
          break;
      }
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
    }
  }

  isEmployee() {
    return USER_SESSION_SERVICE.currentUserRole === PERSON_ROLE_KEYS.EMPLOYEE;
  }

  isProviderManager() {
    return USER_SESSION_SERVICE.currentUserRole === PERSON_ROLE_KEYS.PROVIDER_MANAGER
  }
  /**
   * @method getFarmSitesByTechnicianId
   * @async
   * @description Fetches farm sites associated with a specific technician ID.
   * Used primarily when a Provider Manager selects a technician.
   * @param {string} id - The ID of the technician.
   * @side-effect Updates `farmSites` and auto-selects if only one site exists.
   */
  public async getFarmSitesByTechnicianId(id) {
    try {
      this.pondSummaryReport.farm_site_id = undefined;
      this.farmSites = await this.farmSitesService.getFarmeSitesByTechnicianId(id);
      if (this.farmSites.length === 1) {
        this.pondSummaryReport.farm_site_id = this.farmSites[0].id; // model-binding key
      }
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
    }
  }

  /**
   * @method f
   * @description Getter for easy access to form controls in the HTML template.
   * @returns {any} The controls of the `registerForm`.
   */
  public get f() {
    return this.registerForm.controls;
  }

  /**
   * @method filterBusinesses
   * @description Filters the `businessEntities` list based on the user's input in the business field.
   * Updates `filteredOptions` with the result.
   * @side-effect Updates `filteredOptions`.
   */
  public filterBusinesses(): void {
    const value = (this.registerForm.get('businessfield')!.value || '') as BusinessEntity | string;
    const searchText =
      typeof value === 'string' ? value.toLowerCase() : value.businessName.toLowerCase();
    this.filteredOptions = this.businessEntities.filter(o =>
      o.businessName.toLowerCase().includes(searchText)
    );
  }

  /**
   * @method selectOption
   * @async
   * @description Handles the selection of a business entity from the dropdown.
   * - Updates the form value and model.
   * - Fetches farm sites associated with the selected business.
   * - Auto-selects the farm site if only one exists.
   * @param {BusinessEntity} business - The selected business entity.
   */
  public async selectOption(business: BusinessEntity) {
    this.registerForm.get('businessfield')!.setValue(business.businessName);
    this.isDropdownOpen = false;
    this.businessSearch = business.businessName;
    this.pondSummaryReport.business_id = business.id;
    if (this.pondSummaryReport.business_id) {
      this.pondSummaryReport.farm_site_id = undefined;
      this.farmSites = await this.farmSitesService.getFarmeSitesByFarmsId(this.pondSummaryReport.business_id);
      if (this.farmSites.length === 1) {
        this.pondSummaryReport.farm_site_id = this.farmSites[0].id; // model-binding key
      }
    }
  }

  /**
   * @method onFiltersApplied
   * @description Applies filters to the pond summary list.
   * This is a central method that orchestrates filtering based on:
   * - Text search (`search` property).
   * - Filter type (if applicable).
   * If no filters are active, it resets the list to the initial batch loaded state.
   * @side-effect Updates `pondSummaryReports`, `isFiltering`, `batchLoader`.
   */
  public onFiltersApplied() {
    this.isFiltering = true;
    this.pondSummaryReports = [];
    const value = this.search && this.search.replace(/[-_ :.]/g, '').toLowerCase();
    if (this.search != '') {
      if (this.filterType != '' && this.filtersApplied) {
        this.applySearchWithFilterType();
      } else {
        this.applySearchFilter(value);
      }
    } else {
      if (this.filterType != '' && this.filtersApplied) {
        this.applyFilterWithFilterType();
      } else {
        this.isFiltering = false;
        this.initializeBatchLoader();
        this.batchLoader.reset();
        this.batchLoader.loadNextBatch(true);
      }
    }
  }

  /**
   * @method applySearchFilter
   * @private
   * @description Filters the report list based on a search string.
   * Checks multiple fields: 'section_name', 'pond_name', 'harvest_mode', 'Hatcheryname', 'Breeder', 'Breed_line'.
   * @param {string} searchValue - The normalized search string.
   */
  private applySearchFilter(searchValue: string) {
    this.pondSummaryReports = this.dupPondSummaryReports.filter((pondDetailsObj: any) =>
      ['section_name', 'pond_name', 'harvest_mode', 'Hatcheryname', 'Breeder', 'Breed_line'].some(property => {
        const nestedPropertyValue = property.split('.').reduce((acc, current) => acc && acc[current], pondDetailsObj);
        return nestedPropertyValue && nestedPropertyValue.replace(/[-_ :.]/g, '').toLowerCase().includes(searchValue);
      })
    );
  }

  /**
   * @method applyFilterWithFilterType
   * @private
   * @description Placeholder for applying specific filter types (e.g., status, date) without text search.
   * Currently empty implementation.
   */
  private applyFilterWithFilterType() {

  }

  /**
   * @method applySearchWithFilterType
   * @private
   * @description Placeholder for applying both text search and specific filter types simultaneously.
   * Currently empty implementation.
   */
  private applySearchWithFilterType() {

  }

  /**
   * @method clearFilters
   * @description Clears specific filters based on the input type.
   * @param {string} filterFrom - The source of the clear action ('search' or other).
   */
  public clearFilters(filterFrom: string) {
    switch (filterFrom) {
      case 'search':
        this.search = '';
        this.onFiltersApplied();
        break;
      default:
        this.filterType = '';
        this.filtersApplied = false;
        this.onFiltersApplied();
        break;
    }
  }

  /**
   * @method pondSummary
   * @async
   * @description Fetches the pond summary report data from the server.
   * - Validates the form.
   * - Resets filters and search states.
   * - Calls the service to get data.
   * - Initializes the batch loader for pagination.
   * @throws {Error} Captures and displays errors via `commonMethodsService`.
   */
  public async pondSummary() {
    this.submitted = true;
    this.registerForm.markAllAsTouched();
    if (this.registerForm.invalid) {
      return;
    }
    this.submitted = false;
    this.dupPondSummaryReports = [];
    this.pondSummaryReports = [];
    this.isFiltering = false;
    this.search = '';
    this.isSubmitClicked = false;
    try {
      this.submitButtonDisabled = true;
      this.responseLoader = true;
      if (USER_SESSION_SERVICE.currentUserRole === PERSON_ROLE_KEYS.PROVIDER_MANAGER) {
        let businessId = this.farmSites.find(siteid => siteid.id == this.pondSummaryReport.farm_site_id);
        this.pondSummaryReport.business_id = businessId.businessID;
      }
      this.dupPondSummaryReports = await this.pondSummaryService.getPondSummaryReport(this.pondSummaryReport.farm_site_id);
      this.initializeBatchLoader(); // Reset instance
      this.batchLoader.reset();     // Clear state
      await this.batchLoader.loadNextBatch(true); // Load first batch
      this.submitted = false;    // Reset validation flag
      this.registerForm.markAsPristine(); // optional - clears dirty flags
      this.registerForm.markAsUntouched(); // optional - clears touched state
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
    } finally {
      this.submitButtonDisabled = false;
      this.responseLoader = false;
      this.isSubmitClicked = true;
    }
  }

  /**
   * @method onScroll
   * @description Host listener for window scroll events. Triggers `checkScrollAndLoad` to handle infinite scrolling.
   * @lifecycle HostListener
   */
  @HostListener('window:scroll', [])
  onScroll(): void {
    this.checkScrollAndLoad();
  }

  private scrollTimeout: any;

  /**
   * @method checkScrollAndLoad
   * @private
   * @description Debounced handler for scroll events. Checks if the user has scrolled near the bottom of the page.
   */
  private checkScrollAndLoad(): void {
    clearTimeout(this.scrollTimeout);
    this.scrollTimeout = setTimeout(() => {
      if ((window.innerHeight + window.scrollY) >= document.body.offsetHeight - 100 &&
        !this.batchLoader?.isLoading() && !this.isFiltering) {
        this.batchLoader?.loadNextBatch();
      }
    }, 50);
  }

  /**
   * @method initializeBatchLoader
   * @private
   * @description Initializes the `BatchLoader` utility with the master data list and callback functions for UI updates.
   */
  private initializeBatchLoader(): void {
    this.batchLoader = new BatchLoader<any>(
      () => this.dupPondSummaryReports,
      (updatedList) => {
        this.pondSummaryReports = updatedList;
        this.noList = updatedList.length === 0;
      },
      undefined, // <-- omit or pass undefined
      (loading) => this.listLoader = loading
    );
  }

  /**
    * @method
    * @description Toggles the fullscreen mode for the element with ID 'zoomView'.
    * @returns {void}
    */
  public openFullScreen(): void {
    let element = document.getElementById('zoomView') as HTMLElement;
    if (element.requestFullscreen) {
      element.requestFullscreen();
    }
  }

  /**
   * @method sortingpondsumamry
   * @description Sorts the displayed pond summary list based on the specified type.
   * Supports sorting by 'sectionname' and 'pondname'.
   * @param {string} type - The field to sort by.
   */
  public sortingpondsumamry(type) {
    try {
      if (type == 'sectionname') {
        this.psummarysectionnameAsc = !this.psummarysectionnameAsc;
        let orderbyval = 'desc';
        if (this.psummarysectionnameAsc == true) {
          orderbyval = 'asc';
        }
        this.pondSummaryReports = _.orderBy(this.pondSummaryReports, ['section_name'], [orderbyval]);
        this.psummaryPondnameAsc = false;
      }
      if (type == 'pondname') {
        this.psummaryPondnameAsc = !this.psummaryPondnameAsc;
        let orderbyval = 'desc';
        if (this.psummaryPondnameAsc == true) {
          orderbyval = 'asc';
        }
        this.pondSummaryReports = _.orderBy(this.pondSummaryReports, ['pond_name'], [orderbyval]);
        this.psummarysectionnameAsc = false;
      }
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
    }
  }

  /**
   * @method goToPondsummaryGraph
   * @description Triggers the generation of charts for a specific pond.
   * Calls `diffFeedlogvsDOc` to fetch and process data.
   * @param {string} farmsiteid - The ID of the farm site.
   * @param {number} pondid - The ID of the pond.
   * @param {number} cultureid - The ID of the culture.
   */
  public goToPondsummaryGraph(farmsiteid: string, pondid: number, cultureid: number) {
    try {
      this.diffFeedlogvsDOc(farmsiteid, pondid, cultureid);
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
    }
  }

  /**
   * @method exportExcel
   * @async
   * @description Generates and downloads an Excel report of the pond summary.
   * Constructs the request body with the current farm site ID.
   * @throws {Error} Captures and displays errors via `commonMethodsService`.
   */
  public async exportExcel() {
    const excelReqBody = {
      "farm_site_id": [this.pondSummaryReport.farm_site_id],
    }
    try {
      const fileObj = this.pondSummaryService.excelReportsDownload('pondSummaryReport', excelReqBody);
      fileObj.subscribe(fileblob => {
        if (fileblob) {
          this.excelService.saveAsExcelFile(fileblob, 'Pond Summary Report');
        }
      });
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
    }
  }

  /**
   * @method diffFeedlogvsDOc
   * @async
   * @description Fetches detailed pond culture data and prepares it for charting.
   * Processes Feedlog vs DOC and ABW vs DOC data points.
   * @param {string} farmsiteId - The ID of the farm site.
   * @param {number} pondId - The ID of the pond.
   * @param {number} cultureId - The ID of the culture.
   */
  public async diffFeedlogvsDOc(farmsiteId: string, pondId: number, cultureId: number) {
    try {
      this.responseLoader = true;
      this.myChartNodata = false;
      this.mySamplingvsDocData = false;
      this.pondCulture = await this.pondCultureService.getPondSumaryByFarmSite(farmsiteId, pondId, cultureId);
      if (this.pondCulture !== undefined) {
        // this.FeedgivenvsDoc = res['data'];
        this.responseLoader = false;
        // prepare feedlogvs doc charts
        let docchart = {
          title: 'Feedlog vs DOC',
          lable: [],
          data: [],
          type: 'line',
          fill: true,
          chartid: 'myChart'
        };
        let feedlogDetails = this.pondCulture.feedlogs || [];
        for (let i = 0; i < feedlogDetails.length; i++) {
          let lable = feedlogDetails[i].DOC;
          let data = feedlogDetails[i].feedGiven;
          docchart.lable.push(lable);
          docchart.data.push(data);
        }
        // prepare sampling doc charts
        let samplingdocchart = {
          title: 'ABW vs DOC',
          lable: [],
          data: [],
          type: 'line',
          fill: false,
          chartid: 'samplingChart'
        };
        let samplingDetails = this.pondCulture.samplings || [];
        for (let i = 0; i < samplingDetails.length; i++) {
          let lable = samplingDetails[i].Doc;
          let data = samplingDetails[i].ABW ? samplingDetails[i].ABW : 0;
          samplingdocchart.lable.push(lable);
          samplingdocchart.data.push(data);
        }
        if (docchart.data.length > 0) {
          this.myChartNodata = false;
          this.prepareFeedlogvsDoc(docchart);
        } else {
          this.myChartNodata = true;
        }
        if (samplingdocchart.data.length > 0) {
          this.mySamplingvsDocData = false;
          this.prepareFeedlogvsDoc(samplingdocchart);
        } else {
          this.mySamplingvsDocData = true;
        }
      }
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
    }
  }

  /**
   * @method prepareFeedlogvsDoc
   * @description Initializes and renders a Chart.js chart on the canvas.
   * @param {any} docchart - The configuration object for the chart (labels, data, type, etc.).
   * @side-effect Modifies the DOM by rendering a chart in the canvas element.
   */
  public prepareFeedlogvsDoc(docchart) {
    try {
      this.canvas = document.getElementById(docchart.chartid);
      this.ctx = this.canvas.getContext('2d');
      new Chart(this.ctx, {
        type: docchart.type,
        data: {
          labels: docchart.lable,
          datasets: [{
            label: docchart.title,
            data: docchart.data,
            fill: docchart.fill,
            borderColor: [
              'rgb(84, 216, 255)'
            ],
            borderWidth: 1
          }]
        },
        options: {
          scales: {
            yAxes: [{
              ticks: {
                beginAtZero: true
              }
            }]
          }
        }
      });
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
    }
  }

  /**
   * @lifecycle ngOnDestroy
   * @description Lifecycle hook that is called when the component is destroyed.
   * Cancels ongoing HTTP requests and cleans up the batch loader to prevent memory leaks.
   * @side-effect Cancels HTTP requests, destroys `batchLoader`.
   */
  public ngOnDestroy(): void {
    this.farmSitesService.cancelOnGoingHttpRequests();
    this.businessEntityService.cancelOnGoingHttpRequests();
    this.pondSummaryService.cancelOnGoingHttpRequests();
    this.pondCultureService.cancelOnGoingHttpRequests();
    this.personService.cancelOnGoingHttpRequests();
    this.batchLoader?.markAsDestroyed();
    this.batchLoader?.reset();
  }
}
